import { ChangeEvent, FC, MouseEvent, useMemo, useState } from "react"
import { RadioGroup as RG } from "react-radio-group"
import { Checkbox, Copy, FormFieldGroup, Label, StandardModal, TextInput, colorAttentionBlue50 } from "@rhumbix/rmbx_design_system_web"
import * as API from "../../api"
import { buildSetupOnlyContents, iTimecardTemplate } from "./timecard-template-utils"
import { RadioButtonWithLabel } from "../RadioGroup"
import SelectorWrapper from "../../SelectorWrapper"
import styled from "styled-components"

export type SaveTimecardTemplateModalProps = {
    open: boolean
    closeMethod: () => void
    templates: iTimecardTemplate[]
    onSaved: (template: iTimecardTemplate) => void
    onUpdated: (template: iTimecardTemplate) => void
    projects: Array<{ id: number }>
    employees: Array<{ id: number }>
    costCodes: Array<{ id: number }>
    shiftStartTime: Date | null
    shiftEndTime: Date | null
}

type SaveMode = "new" | "update"

/**
 * Save the create-time-card modal's current setup selections (projects, employees, cost
 * codes, shift times) as a reusable TimecardTemplate, either as a new template or overwriting
 * an existing one. Since this modal is used before any WorkShift exists, the saved contents
 * have no entries/shift_extras/absences -- only `setup` and `timecard.shift_times`.
 */
const SaveTimecardTemplateModal: FC<SaveTimecardTemplateModalProps> = props => {
    const [mode, setMode] = useState<SaveMode>("new")
    const [name, setName] = useState("")
    const [templateToUpdate, setTemplateToUpdate] = useState<iTimecardTemplate | null>(null)
    const [includeProjects, setIncludeProjects] = useState(true)
    const [includeWorkers, setIncludeWorkers] = useState(true)
    const [includeCostCodes, setIncludeCostCodes] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    const templateOptionList = useMemo(
        () =>
            props.templates.map(template => ({
                value: template.id,
                label: template.name,
                data: template,
            })),
        [props.templates]
    )

    const resetAndClose = () => {
        setMode("new")
        setName("")
        setTemplateToUpdate(null)
        setErrorMessage(undefined)
        props.closeMethod()
    }

    const onClickSave = () => {
        if (!props.shiftStartTime || !props.shiftEndTime) return
        const contents = buildSetupOnlyContents(
            includeProjects ? props.projects : [],
            includeWorkers ? props.employees : [],
            includeCostCodes ? props.costCodes : [],
            props.shiftStartTime,
            props.shiftEndTime,
            Intl.DateTimeFormat().resolvedOptions().timeZone
        )

        if (mode === "update") {
            if (!templateToUpdate) return
            API.updateTimecardTemplate(templateToUpdate.id, { contents })
                .then((template: iTimecardTemplate) => {
                    props.onUpdated(template)
                    resetAndClose()
                })
                .catch(() => setErrorMessage("Could not update this template."))
            return
        }

        API.createTimecardTemplate({ name, contents })
            .then((template: iTimecardTemplate) => {
                props.onSaved(template)
                resetAndClose()
            })
            .catch((error: { response?: { data?: { errors?: { name?: string[] } } } }) => {
                setErrorMessage(error.response?.data?.errors?.name?.[0] ?? "Could not save this template.")
            })
    }

    const onModeRadioClick = (event: MouseEvent<HTMLInputElement>) => {
        setErrorMessage(undefined)
        setMode(event.currentTarget.value as SaveMode)
    }

    // SelectorWrapper invokes this with the {value,label,data} wrapper for the closed-box display,
    // but with the raw template (the wrapper's `data`) for each dropdown row -- handle both shapes.
    const formatTemplateOption = (option: { value?: { label?: string; name?: string } | null }) => option.value?.label ?? option.value?.name ?? ""

    return (
        <StandardModal
            id="save-timecard-template-modal"
            autoClose={false}
            open={props.open}
            closeMethod={resetAndClose}
            title={"Save Time Card Template"}
            description={"Save this timecard's setup as a reusable template, either as a new template or by updating one you already have."}
            primaryBtnText={"Save Template"}
            primaryBtnOnClick={onClickSave}
            primaryBtnDisabled={mode === "update" ? !templateToUpdate : !name.trim()}
            primaryBtnColor={colorAttentionBlue50}
            secondaryBtnOnClick={resetAndClose}
            width={"550px"}
        >
            <RG name="timecard-template-save-mode" selectedValue={mode}>
                <ModeRow>
                    <RadioButtonWithLabel
                        id="timecard-template-save-mode-update"
                        value={"update"}
                        labelText={"Update an existing template"}
                        checked={mode === "update"}
                        disabled={props.templates.length === 0}
                        onClick={onModeRadioClick}
                    />
                    <SelectorContainer $dimmed={mode !== "update"}>
                        <SelectorWrapper
                            id="timecard-template-to-update-selector"
                            name="timecard-template-to-update-selector"
                            optionList={templateOptionList}
                            value={templateOptionList.find(option => option.value === templateToUpdate?.id)}
                            onChange={(template: unknown) => setTemplateToUpdate((template as iTimecardTemplate | null | undefined) ?? null)}
                            valueFormatter={formatTemplateOption}
                            titleFormatter={formatTemplateOption}
                            isClearable={true}
                            isMulti={false}
                            isDisabled={mode !== "update" || props.templates.length === 0}
                        />
                    </SelectorContainer>
                </ModeRow>
                <ModeRow>
                    <RadioButtonWithLabel id="timecard-template-save-mode-new" value={"new"} labelText={"Save as a new template"} checked={mode === "new"} onClick={onModeRadioClick} />
                    <NameInputContainer $dimmed={mode !== "new"}>
                        <TextInput
                            id="timecard-template-name"
                            value={name}
                            disabled={mode !== "new"}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setErrorMessage(undefined)
                                setName(event.target.value)
                            }}
                        />
                    </NameInputContainer>
                </ModeRow>
            </RG>
            <FormFieldGroup>
                <Label>Included Data</Label>
                <IncludedDataRow>
                    <IncludedDataOption>
                        <Checkbox checked={includeProjects} onChange={setIncludeProjects} />
                        <Copy>Projects</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={includeWorkers} onChange={setIncludeWorkers} />
                        <Copy>Workers</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={includeCostCodes} onChange={setIncludeCostCodes} />
                        <Copy>Cost Codes</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={false} disabled={true} onChange={() => undefined} />
                        <Copy>Hours</Copy>
                    </IncludedDataOption>
                </IncludedDataRow>
            </FormFieldGroup>
            {errorMessage && <Copy>{errorMessage}</Copy>}
        </StandardModal>
    )
}

export default SaveTimecardTemplateModal

const ModeRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
`

const SelectorContainer = styled.div<{ $dimmed: boolean }>`
    width: 260px;
    opacity: ${props => (props.$dimmed ? 0.5 : 1)};
`

const NameInputContainer = styled.div<{ $dimmed: boolean }>`
    width: 260px;
    opacity: ${props => (props.$dimmed ? 0.5 : 1)};
`

const IncludedDataRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
`

const IncludedDataOption = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`
